// =============================================================================
// authorPhotoGenerator — dwuetapowy generator zdjecia autora.
//
//   Etap 1: Opus 4.8 pisze prompt obrazkowy z danych mapy (audience + opis serwisu
//           + profil autora) -> uzytkownik moze go recznie poprawic.
//   Etap 2: FAL Seedream 4.5 generuje N propozycji z TEGO promptu -> uzytkownik
//           wybiera, ktore zapisac do biblioteki (bucket 'author-photos').
//
// CELOWO NIE uzywa falaiService — tamten dokleja flux-owe pola (guidance_scale,
// num_inference_steps, safety_tolerance), ktorych Seedream nie zna (ryzyko 422),
// i obsluguje obrazki wyrozniajace artykulow (zero powodu, zeby go ruszac).
//
// sync_mode: true -> FAL zwraca data URI zamiast URL-a. Dzieki temu zapis do
// Storage nie potrzebuje cross-origin fetcha, a CSP (img-src 'self' data: https:)
// pokazuje podglad bez wyjatkow.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { getActiveWorkspace } from "@/lib/workspace";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, parseLLMJSON } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";

/** Endpoint modelu na fal.run (przez proxy /api/fal-ai/). */
export const SEEDREAM_ENDPOINT = "fal-ai/bytedance/seedream/v4.5/text-to-image";

/** Rozmiary z enuma FAL: square = 512x512, square_hd = 1024x1024. */
export type AuthorPhotoSize = "square" | "square_hd";

/** Seedream przyjmuje 1-6 na request — my i tak strzelamy po 1 obrazku na request. */
export const MAX_PHOTOS_PER_RUN = 8;

export interface AuthorPhotoContext {
  mapId: string;
  serviceName: string;
  siteUrl: string;
  siteDescription: string;
  macroStatement: string;
  audience: string;
  language: string;
  authorName: string;
  authorLabel: string;
  authorIdentity: string;
  authorMission: string;
  authorAudience: string;
}

// =============================================================================
// Kontekst — read-only, scoped do map_id
// =============================================================================

export async function fetchAuthorPhotoContext(mapId: string): Promise<AuthorPhotoContext | null> {
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("id, name, macro_statement, audience, language, website_id, active_profile_id")
    .eq("id", mapId)
    .maybeSingle();
  if (!map) return null;

  let serviceName = map.name || "";
  let siteUrl = "";
  let siteDescription = "";
  if (map.website_id) {
    const { data: site } = await (supabase as any)
      .from("websites")
      .select("name, url, organization_description")
      .eq("id", map.website_id)
      .maybeSingle();
    if (site) {
      serviceName = site.name || site.url || serviceName;
      siteUrl = site.url || "";
      siteDescription = site.organization_description || "";
    }
  }

  let authorName = "";
  let authorLabel = "";
  let authorIdentity = "";
  let authorMission = "";
  let authorAudience = "";
  if (map.active_profile_id) {
    const { data: prof } = await (supabase as any)
      .from("thematic_writing_profiles")
      .select("public_author_label, profile_name, name, author_identity, mission, audience_primary")
      .eq("id", map.active_profile_id)
      .maybeSingle();
    if (prof) {
      authorName = prof.profile_name || prof.name || "";
      authorLabel = prof.public_author_label || "";
      authorIdentity = prof.author_identity || "";
      authorMission = prof.mission || "";
      authorAudience = prof.audience_primary || "";
    }
  }

  // Persona (krok "Autor info") ma pierwszenstwo dla imienia — to je user wpisal recznie.
  const { data: persona } = await (supabase as any)
    .from("map_persona")
    .select("full_name")
    .eq("map_id", mapId)
    .maybeSingle();
  if (persona?.full_name) authorName = persona.full_name;

  return {
    mapId,
    serviceName,
    siteUrl,
    siteDescription,
    macroStatement: map.macro_statement || "",
    audience: map.audience || "",
    language: map.language || "pl",
    authorName,
    authorLabel,
    authorIdentity,
    authorMission,
    authorAudience,
  };
}

// =============================================================================
// Etap 1 — prompt obrazkowy (Opus 4.8)
// =============================================================================

const NATIONALITY_BY_LANG: Record<string, string> = {
  pl: "polish",
  en: "british",
  de: "german",
  cs: "czech",
  sk: "slovak",
  uk: "ukrainian",
  es: "spanish",
  fr: "french",
  it: "italian",
};

/** Pierwsze slowo z pelnego imienia i nazwiska (do promptu NIGDY nie idzie nazwisko). */
export function firstNameOnly(fullName: string): string {
  return (fullName || "").trim().split(/\s+/)[0] || "";
}

/**
 * Deterministyczny fallback — uzywany gdy nie ma promptu w DB, kompilacja padnie
 * albo LLM zwroci smiecie. Ten sam anty-stockowy styl co prompt z bazy.
 */
export function buildFallbackPhotoPrompt(ctx: AuthorPhotoContext): string {
  const nationality = NATIONALITY_BY_LANG[ctx.language?.toLowerCase()] || "european";
  const name = firstNameOnly(ctx.authorName) || "Alex";
  const role = ctx.authorLabel || ctx.macroStatement || ctx.serviceName || "content author";
  return [
    `amateur photo of ${name}, a 38 year old ${nationality} person,`,
    `${role},`,
    "natural everyday hairstyle, warm eyes with subtle laugh lines, genuine relaxed smile,",
    "visible skin pores and natural skin texture with minor imperfections, slightly asymmetrical face,",
    "wearing simple smart casual clothes, standing indoors with a softly blurred realistic background,",
    "warm natural daylight, chest-up framing, relaxed confident pose,",
    "shot on smartphone, candid editorial portrait, no studio lighting, photorealistic,",
    "natural skin texture, slight grain, no model good looks, real everyday look,",
    "no text, no watermark, no logos, single person",
  ].join(" ");
}

export interface BuildPromptResult {
  prompt: string;
  source: "llm" | "fallback";
  costUsd?: number;
}

/** Etap 1: zbuduj prompt obrazkowy. Nigdy nie rzuca — w najgorszym razie fallback. */
export async function buildAuthorPhotoPrompt(mapId: string): Promise<BuildPromptResult> {
  const ctx = await fetchAuthorPhotoContext(mapId);
  if (!ctx) throw new Error("Nie znaleziono mapy");

  const fallback = buildFallbackPhotoPrompt(ctx);

  const dbPrompt = await loadPrompt("author_photo_prompt" as any);
  if (!dbPrompt) {
    logError("AUTHOR-PHOTO", "Brak aktywnego promptu 'author_photo_prompt' — fallback");
    return { prompt: fallback, source: "fallback" };
  }

  const compiled = compilePromptSafe(
    dbPrompt.template,
    {
      service_name: ctx.serviceName,
      site_url: ctx.siteUrl,
      site_description: ctx.siteDescription,
      macro_statement: ctx.macroStatement,
      audience: ctx.audience,
      language: ctx.language,
      author_name: ctx.authorName,
      author_label: ctx.authorLabel,
      author_identity: ctx.authorIdentity,
      author_mission: ctx.authorMission,
      author_audience: ctx.authorAudience,
    },
    "author_photo_prompt",
    fallback,
  );
  if (compiled === fallback) {
    logError("AUTHOR-PHOTO", "Kompilacja promptu nie powiodla sie (unresolved vars) — fallback");
    return { prompt: fallback, source: "fallback" };
  }

  try {
    const provider = getLLMProvider((dbPrompt.provider || "claude") as "openai" | "claude");
    const resp = await provider.call(compiled, {
      model: dbPrompt.modelId || "claude-opus-5",
      // NULL w DB = nie narzucamy probkowania. Opus 4.7/4.8 i tak odrzuca temperature
      // (400) — claudeProvider.supportsSampling() zdejmuje ja z body.
      temperature: dbPrompt.temperature ?? undefined,
      step: "author_photo_prompt",
      mapId,
    });

    let text = "";
    try {
      text = (parseLLMJSON<{ prompt?: string }>(resp.text)?.prompt || "").trim();
    } catch {
      text = (resp.text || "").trim(); // model zwrocil goly tekst zamiast JSON-a
    }

    if (text.length < 40) {
      logError("AUTHOR-PHOTO", `LLM zwrocil za krotki prompt (${text.length} zn.) — fallback`);
      return { prompt: fallback, source: "fallback" };
    }

    logPublic("AUTHOR-PHOTO", `Prompt zbudowany (${text.length} zn., $${(resp.costUsd ?? 0).toFixed(4)})`);
    return { prompt: text, source: "llm", costUsd: resp.costUsd };
  } catch (e) {
    logError("AUTHOR-PHOTO", "LLM padl przy budowaniu promptu — fallback", e);
    return { prompt: fallback, source: "fallback" };
  }
}

// =============================================================================
// Etap 2 — obrazki z FAL Seedream 4.5
// =============================================================================

/** Czysty payload Seedream — bez flux-owych pol, ktore ten model odrzuca. */
export function buildSeedreamPayload(prompt: string, imageSize: AuthorPhotoSize) {
  return {
    prompt,
    image_size: imageSize,
    num_images: 1,
    max_images: 1,
    sync_mode: true,
    enable_safety_checker: true,
  };
}

async function generateOnePhoto(prompt: string, imageSize: AuthorPhotoSize): Promise<string> {
  const resp = await fetch(`/api/fal-ai/${SEEDREAM_ENDPOINT}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // nginx /api/fal-ai/ nadpisuje Authorization kluczem per workspace
      Authorization: "Key ",
      "X-Workspace": getActiveWorkspace(),
    },
    body: JSON.stringify(buildSeedreamPayload(prompt, imageSize)),
  });

  if (!resp.ok) {
    const body = await resp.text();
    throw new Error(`FAL ${resp.status}: ${body.slice(0, 200)}`);
  }

  const json = await resp.json();
  const src = json?.images?.[0]?.url;
  if (!src || typeof src !== "string") throw new Error("FAL nie zwrocil obrazka");
  return src;
}

export interface GeneratePhotosResult {
  ok: number;
  failed: number;
  errors: string[];
}

/**
 * Odpala N NIEZALEZNYCH requestow (po 1 obrazku) rownolegle — kazdy dostaje inny
 * seed, wiec propozycje sie roznia, a jeden blad nie zabija reszty. Kazdy gotowy
 * obrazek leci od razu do onImage() (progresywne dokladanie w UI).
 */
export async function generateAuthorPhotos(opts: {
  prompt: string;
  count: number;
  imageSize: AuthorPhotoSize;
  onImage?: (src: string) => void;
}): Promise<GeneratePhotosResult> {
  const count = Math.max(1, Math.min(MAX_PHOTOS_PER_RUN, opts.count));
  const errors: string[] = [];
  let ok = 0;
  let failed = 0;

  const results = await Promise.allSettled(
    Array.from({ length: count }, () =>
      generateOnePhoto(opts.prompt, opts.imageSize).then((src) => {
        opts.onImage?.(src);
        return src;
      }),
    ),
  );

  for (const r of results) {
    if (r.status === "fulfilled") ok++;
    else {
      failed++;
      const msg = r.reason instanceof Error ? r.reason.message : String(r.reason);
      if (!errors.includes(msg)) errors.push(msg);
    }
  }

  logPublic("AUTHOR-PHOTO", `Wygenerowano ${ok}/${count} propozycji (bledy: ${failed})`);
  return { ok, failed, errors };
}

// =============================================================================
// Zapis — konwersja do lekkiego JPEG przed uploadem
// =============================================================================

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Nie udalo sie wczytac obrazka"));
    img.src = src;
  });
}

/**
 * data URI -> JPEG File. 512x512 PNG z FAL wazy ~400 KB, ten sam kadr w JPEG
 * q0.85 to ~40-70 KB — czyli od razu rozmiar nadajacy sie pod WordPress.
 * Canvas nie jest "tainted", bo zrodlo to data URI (same-origin).
 */
export async function dataUrlToJpegFile(
  dataUrl: string,
  fileName: string,
  quality = 0.85,
): Promise<File> {
  const img = await loadImage(dataUrl);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Brak kontekstu canvas");
  // JPEG nie ma kanalu alfa — bez bialego tla przezroczystosc wyszlaby na czarno
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob) throw new Error("Konwersja do JPEG nie powiodla sie");
  return new File([blob], fileName, { type: "image/jpeg" });
}

/** Nazwa pliku dla zapisanej propozycji (authorPhotos.safeName i tak ja przemieli). */
export function photoFileName(index: number): string {
  return `autor-ai-${index + 1}-${Date.now()}.jpg`;
}

/**
 * Zamien zrodlo propozycji na plik do uploadu.
 * data URI -> lekki JPEG (canvas). Zwykly URL (gdyby sync_mode nie zadzialal)
 * -> pobierz bajty jak sa; bez canvasa, zeby nie wywrocic sie na cross-origin.
 */
export async function proposalToFile(src: string, index: number): Promise<File> {
  if (src.startsWith("data:")) {
    return dataUrlToJpegFile(src, photoFileName(index));
  }
  const resp = await fetch(src);
  if (!resp.ok) throw new Error(`Nie udalo sie pobrac obrazka (${resp.status})`);
  const blob = await resp.blob();
  const type = blob.type || "image/png";
  const ext = type.includes("jpeg") ? "jpg" : "png";
  return new File([blob], photoFileName(index).replace(/\.jpg$/, `.${ext}`), { type });
}
